/**
 * Hızlı satış indirim kaydı yardımcıları.
 *
 * İndirim, sepet kaleminin `price` alanına gömülüdür; indirim öncesi fiyat `originalPrice`'ta
 * tutulur. Satışı kaydederken indirimi hem muhasebe faturasına (kalem) hem de hızlı satış fişine
 * (başlık) GERÇEK indirim olarak yazmak için gereken alanları üretir.
 *
 * `originalPrice`/`price` kalemin KDV tabanındadır (VAT_INCLUDED → KDV dahil, VAT_EXCLUDED → KDV
 * hariç). Backend (InvoiceManager) indirimi aynı tabanda uygular; bu sayede çift sayım olmaz ve
 * fatura matrahı/KDV/genel toplam doğru hesaplanır.
 */

export interface ItemDiscountFields {
  OriginalPrice: number | null;
  DiscountRate: number;
  DiscountAmount: number;
}

export interface HeaderDiscountFields {
  DiscountType: string | null; // 'PERCENTAGE' | 'AMOUNT' | null
  DiscountRate: number;
  DiscountAmount: number;
}

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Sepet kaleminden kalem-seviyesi indirim alanlarını üretir (faturaya gerçek indirim için).
 */
export const buildItemDiscountFields = (
  cartItem: { originalPrice?: number; price: number; quantity: number }
): ItemDiscountFields => {
  const orig = cartItem.originalPrice;
  const price = cartItem.price;
  if (orig !== undefined && orig > price) {
    const discountAmount = roundTo2((orig - price) * cartItem.quantity);
    const discountRate = orig > 0 ? roundTo2(((orig - price) / orig) * 100) : 0;
    return { OriginalPrice: orig, DiscountRate: discountRate, DiscountAmount: discountAmount };
  }
  return { OriginalPrice: null, DiscountRate: 0, DiscountAmount: 0 };
};

/**
 * Sepet ve uygulanan indirim bilgisinden fiş başlığı indirim alanlarını üretir.
 *
 * ⚠️ ÖNEMLİ: İndirim tutarı (DiscountAmount), state (`lastAppliedDiscountAmount`) yerine
 * DOĞRUDAN sepetten (`originalPrice` − `price`) hesaplanır. Çünkü kalem indirimi de aynı
 * kaynaktan üretiliyor; state bağımlılığı (stale closure vb.) sebebiyle header indiriminin
 * 0 kalması sorununu önler. DiscountType/DiscountRate ise uygulanan moddan türetilir.
 * Tutar KDV DAHİL (brüt) olarak hesaplanır — fiş "indirimsiz toplam − indirim = toplam" görünür.
 */
export const buildHeaderDiscountFields = (
  cart: Array<{ originalPrice?: number; price: number; quantity: number; vatRate?: number; vatStatus?: string }>,
  appliedDiscountType: 'percentage' | 'amount' | null,
  lastAppliedDiscountAmount: number
): HeaderDiscountFields => {
  // Sepetteki gerçek indirimi originalPrice/price farkından (KDV dahil) hesapla
  const grossDiscountFromCart = cart.reduce((s, it) => {
    const orig = it.originalPrice;
    if (orig !== undefined && orig > it.price) {
      const vr = (it.vatRate || 0) / 100;
      const origWithVat = it.vatStatus === 'included' ? orig : orig * (1 + vr);
      const priceWithVat = it.vatStatus === 'included' ? it.price : it.price * (1 + vr);
      return s + (origWithVat - priceWithVat) * it.quantity;
    }
    return s;
  }, 0);

  // Sepetten hesaplanamadıysa (ör. originalPrice yoksa) state'teki değere düş
  const discountAmount = grossDiscountFromCart > 0
    ? roundTo2(grossDiscountFromCart)
    : (lastAppliedDiscountAmount > 0 ? roundTo2(lastAppliedDiscountAmount) : 0);

  // 🟪 [İNDİRİM-TEŞHİS] Bu log görünüyorsa FIX 1 (cart-based) AKTİF demektir.
  console.log('🟪 [İNDİRİM-TEŞHİS] buildHeaderDiscountFields', {
    grossDiscountFromCart,
    lastAppliedDiscountAmount,
    appliedDiscountType,
    hesaplananDiscountAmount: discountAmount,
    cartOzet: cart.map(c => ({ price: c.price, originalPrice: c.originalPrice, qty: c.quantity, vatStatus: c.vatStatus })),
  });

  if (!(discountAmount > 0)) {
    return { DiscountType: null, DiscountRate: 0, DiscountAmount: 0 };
  }

  // Tip bilinmiyorsa AMOUNT varsay (tutar bazlı gösterim güvenli varsayılan)
  const DiscountType = appliedDiscountType === 'percentage' ? 'PERCENTAGE' : 'AMOUNT';
  let DiscountRate = 0;
  if (appliedDiscountType === 'percentage') {
    const origTotalWithVat = cart.reduce((s, it) => {
      const orig = it.originalPrice !== undefined ? it.originalPrice : it.price;
      const vr = (it.vatRate || 0) / 100;
      const withVat = it.vatStatus === 'included' ? orig : orig * (1 + vr);
      return s + withVat * it.quantity;
    }, 0);
    DiscountRate = origTotalWithVat > 0 ? roundTo2((discountAmount / origTotalWithVat) * 100) : 0;
  }
  return { DiscountType, DiscountRate, DiscountAmount: discountAmount };
};
